import React, { useLayoutEffect, useRef, MutableRefObject } from 'react';
import styled from '@emotion/styled';
import { select } from 'd3-selection';
import { drag, D3DragEvent } from 'd3-drag';
import { timeFormat } from 'd3-time-format';
import { ScaleTime } from 'd3-scale';
import { ZoomHelper } from '../helpers/Zoom.helper';

const Path = styled.path`
  cursor: pointer;
  transition: transform 200ms ease-in-out;

  &.flip-left {
    transform: translate(0, 0) scale(-1, 1);
  }

  &.flip-right {
    transform: translate(0, 0);
  }
`;

const Text = styled.text`
  font-size: 0.75rem;
  fill: #fff;
  text-anchor: middle;
  pointer-events: none;
  transition: transform 200ms ease-in-out;

  &.flip-text-left {
    transform: translate(-24px, 0px);
  }

  &.flip-text-right {
    transform: translate(24px, 0);
  }
`;

interface Props {
  x: number;
  y: number;
  widthBoundary: number;
  clsName: string;
  onMoveHandle: (x1: number, x2: number) => void;
  xScale: ScaleTime<number, number>;
}

export const ZoomFlag = (props: Props) => {
  const formatText = timeFormat('%H:%M');
  const { x, y, widthBoundary, clsName, onMoveHandle, xScale } = props;

  const handle = useRef(null) as MutableRefObject<SVGGElement> | MutableRefObject<null>;
  const handleText = useRef(null) as MutableRefObject<SVGTextElement> | MutableRefObject<null>;
  const flag = useRef(null) as MutableRefObject<SVGPathElement> | MutableRefObject<null>;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  function dragended() {
    const x1 = ZoomHelper.parseTransform(select('g.zoom-handle-left').attr('transform'));
    const x2 = ZoomHelper.parseTransform(select('g.zoom-handle-right').attr('transform'));

    onMoveHandle(x1, x2);
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  function dragged(this: SVGGElement, event: D3DragEvent<SVGGElement, unknown, unknown>) {
    if (flag.current && handleText.current) {
      if (clsName === 'right' && event.x < widthBoundary - 49) {
        ZoomHelper.flipRight(flag.current, handleText.current);
      } else if (clsName === 'left' && event.x > 49) {
        ZoomHelper.flipLeft(flag.current, handleText.current);
      } else if (clsName === 'right' && event.x > widthBoundary - 49) {
        ZoomHelper.flipLeft(flag.current, handleText.current);
      } else if (clsName === 'left' && event.x < 49) {
        ZoomHelper.flipRight(flag.current, handleText.current);
      }
    }

    const oppositeFlagX = ZoomHelper.getOppositeFlagX(clsName);

    if (event.x > 0 && event.x < widthBoundary) {
      if (
        (clsName === 'left' && event.x < oppositeFlagX) ||
        (clsName === 'right' && event.x > oppositeFlagX)
      ) {
        select(this).attr('transform', `translate(${event.x}, -28)`);

        if (handleText.current) {
          handleText.current.textContent = formatText(new Date(xScale.invert(event.x)));
        }
      }
    }
  }

  useLayoutEffect(() => {
    select<SVGGElement, unknown>(`g.zoom-handle-${clsName}`).call(
      drag<SVGGElement, unknown>()
        .on('drag', dragged)
        .on('end', dragended)
    );
  }, [clsName, dragended, dragged, widthBoundary, x]);

  return (
    <g
      ref={handle}
      className={`zoom-handle-${clsName}`}
      transform={`translate(${clsName === 'right' ? x : 0}, -28)`}
      data-testid={`zoom-flag-${clsName}`}
    >
      <Path
        ref={flag}
        d="M0,0 L0,28 L6,21 L48,21 L48,0"
        stroke="#4694CE"
        fill="#4694CE"
        className={`${clsName === 'right' ? 'flip-left' : 'flip-right'}`}
      />
      <Text
        ref={handleText}
        className={`zoom-handle-text-${clsName} ${
          clsName === 'right' ? 'flip-text-left' : 'flip-text-right'
        }`}
        y={y}
        dy="10px"
      >
        {handleText.current && formatText(new Date(xScale.invert(x)))}
      </Text>
    </g>
  );
};

export default ZoomFlag;
